/**
 * SMS alert-types service over the typed `@tiaki/http-client`
 * per ADR 113 §Decision (call-site path autocomplete).
 *
 * `uploadAlertTypeFile` retains a FormData upload via `client.ky` — multipart
 * uploads bypass the typed `client.PATCH` body shape but still pick up the
 * lib's auth-refresh + GPC + vendor-org middleware.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

/**
 * Per-AlertType drill tone.
 *
 * Deliberately SEPARATE from `mp3_file` / `ogg_file`. Those two carry the
 * real-alert audio; `drill_sound` only selects which tone plays when the
 * alert type is activated as a DRILL. Choosing a drill tone must never
 * change what a real alert sounds like, which is why this is a scalar enum
 * on the JSON body and not another multipart upload slot.
 *
 * Wire values are frozen with the backend half. Members are appended, never
 * renumbered or renamed — these exact strings key the bundled audio in four
 * clients (AIM web, SMS web, Android, iOS), so a rename here is a wire break,
 * not a refactor.
 */
export type DrillSound = 'same_as_alert' | 'drill_1' | 'drill_2' | 'drill_3';

export const DRILL_SOUND_DEFAULT: DrillSound = 'same_as_alert';

/**
 * Per-AlertType tone for a REAL alert — the exact
 * mirror of `DrillSound` on the other side of the drill/real split, frozen
 * with the backend half.
 *
 * The two sentinels are not tones:
 * - `default` is the column default and means "resolve exactly as you did
 *   before this feature existed" — the custom upload when the type has one,
 *   otherwise the client's own bundled alert tone. A row left at `default`
 *   produces a byte-identical payload to the pre-feature output.
 * - `custom_upload` is the durable form of the same intent: pin the
 *   admin-uploaded file even as the bundled library grows. It never changes
 *   what `mp3_file` contains, only which available sound the client prefers.
 *
 * `alert_1` is the INCUMBENT tone — the asset the clients already ship as
 * `alert` — not a new one, which is why this app bundles no `alert-1.mp3`.
 */
export type AlertSound =
  'default' | 'custom_upload' | 'alert_1' | 'alert_2' | 'alert_3';

export const ALERT_SOUND_DEFAULT: AlertSound = 'default';

export interface AlertType {
  id: number;
  name: string;
  school: number;
  icon?: string | null;
  sort_order: number;
  urgency: 'emergency' | 'attention' | 'follow';
  activation_mode: 'alert' | 'incident';
  is_active: boolean;
  requires_admin_approval?: boolean;
  is_reverse?: boolean;
  is_default?: boolean;
  is_missing_default?: boolean;
  prompt_location?: boolean;
  can_start_others_group_alert?: boolean;
  elevate_group_members?: boolean;
  alert_to_specific_groups_only?: number[];
  mp3_file?: string | null;
  ogg_file?: string | null;
  /** Drill tone selector — see `DrillSound`. Orthogonal to the two
   *  file fields above; a writer that sets this must not touch either.
   *
   *  OPTIONAL because the backend's `SoundLibraryGateMixin` DROPS this key
   *  from the rendered payload when the org has not launched the sound
   *  library — it is not merely null, it is absent. Decode it as missing and
   *  fall back to `DRILL_SOUND_DEFAULT`. */
  drill_sound?: DrillSound;
  /** Real-alert tone selector — see `AlertSound`. Same gate, same
   *  absence semantics as `drill_sound`: fall back to `ALERT_SOUND_DEFAULT`
   *  when the key is missing, never render a blank picker. Also orthogonal to
   *  `mp3_file` / `ogg_file` — selecting a library tone leaves the uploaded
   *  audio exactly where it was. */
  alert_sound?: AlertSound;
  /** ADR-144 Phase 1: plain fallback text broadcast for the alert type. */
  announce_text?: string | null;
  /** ADR-144 Phase 1: template string with {{intake.<slug>}} placeholders. */
  announce_template?: string | null;
  data?: { radius?: { value: string; unit: string } };
  deleted?: string | null;
  is_system?: boolean;
}

export interface AlertTypeListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AlertType[];
}

export interface AlertTypeFilters {
  school?: number;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  deleted?: boolean;
  is_active?: boolean;
  include_system?: boolean;
}

export async function getAlertTypes(
  filters: AlertTypeFilters = {},
): Promise<AlertTypeListResponse> {
  const query: Record<string, string | number | boolean> = {};
  if (filters.school) query.school = filters.school;
  if (filters.search) query.query = filters.search;
  if (filters.page) query.page = filters.page;
  if (filters.page_size) query.page_size = filters.page_size;
  if (filters.ordering) query.ordering = filters.ordering;
  if (filters.deleted !== undefined) query.deleted = filters.deleted;
  if (filters.include_system) query.include_system = filters.include_system;

  const result = await client.GET('/api/v2/alert-type/', {
    params: { query: query as never },
  });
  return unwrap(result) as unknown as AlertTypeListResponse;
}

export async function getAlertTypeById(id: number): Promise<AlertType> {
  const result = await client.GET('/api/v2/alert-type/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as AlertType;
}

/**
 * This writer forwards `data` VERBATIM — it does not rebuild the body
 * from a hand-listed set of keys. Keep it that way. The AIM sibling
 * (`apps/aegix-aim-react/src/main/services/alert/index.tsx` `createAlertType`)
 * does enumerate its keys, so any field added to the form but not to that list
 * saves on edit and silently vanishes on create. A new scalar column here only
 * needs the `AlertType` interface extended; the test suite pins the
 * pass-through so a future "tidy-up" into an allowlist trips CI instead of
 * quietly dropping fields.
 */
export async function createAlertType(
  data: Partial<Omit<AlertType, 'id'>>,
): Promise<AlertType> {
  const result = await client.POST('/api/v2/alert-type/', {
    body: data as never,
  });
  return unwrap(result) as unknown as AlertType;
}

export async function updateAlertType(
  id: number,
  data: Partial<Omit<AlertType, 'id'>>,
): Promise<AlertType> {
  const result = await client.PATCH('/api/v2/alert-type/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as AlertType;
}

export async function uploadAlertTypeFile(
  id: number,
  formData: FormData,
): Promise<AlertType> {
  // Multipart upload — bypass openapi-fetch's typed body in favor of ky
  // direct so the browser sets Content-Type with the boundary.
  const response = await client.ky.patch(`api/v2/alert-type/${id}/`, {
    body: formData,
  });
  return (await response.json()) as AlertType;
}

export async function deleteAlertType(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/alert-type/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

export async function restoreAlertType(id: number): Promise<AlertType> {
  const result = await client.PATCH('/api/v2/alert-type/{id}/', {
    params: { path: { id } as never },
    body: { deleted: null } as never,
  });
  return unwrap(result) as unknown as AlertType;
}
